import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MonoLabel, Pill, ProgressBar } from '../primitives';
import { statusToPill } from './helpers';
import type { ProjectListItem } from '../../lib/projects/useProjectsList';
import type { ProjectsGroupBy } from '../../lib/models/projects';

/** Card chip / property keys for the Gallery view's Properties picker. */
export const GALLERY_CHIP_KEYS = [
  'status',
  'lead',
  'progress',
  'dod',
  'target_date',
  'description'
] as const;

export type GalleryChipKey = (typeof GALLERY_CHIP_KEYS)[number];

export const GALLERY_CHIP_LABELS: Readonly<Record<GalleryChipKey, string>> = {
  status: 'Status',
  lead: 'Lead',
  progress: 'Progress',
  dod: 'Definition of Done',
  target_date: 'Target date',
  description: 'Description'
};

export const GALLERY_DEFAULT_CHIPS: ReadonlyArray<GalleryChipKey> = [
  'status',
  'lead',
  'progress',
  'dod',
  'description'
];

export interface ProjectsGalleryProps {
  rows: ReadonlyArray<ProjectListItem>;
  /** Subset of chip keys to render on each card. */
  visibleChips: ReadonlyArray<string>;
  /** Section grouping, or 'none' for a flat grid. */
  groupBy: ProjectsGroupBy;
  onRowOpen: (id: string) => void;
}

interface GroupedRows {
  key: string;
  label: string;
  rows: ReadonlyArray<ProjectListItem>;
}

function groupRows(
  rows: ReadonlyArray<ProjectListItem>,
  groupBy: ProjectsGroupBy
): GroupedRows[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: '', rows }];
  }
  const groups = new Map<string, { label: string; rows: ProjectListItem[] }>();
  for (const it of rows) {
    let key: string;
    let label: string;
    switch (groupBy) {
      case 'status':
        key = it.project.status || 'unset';
        label = it.project.status || 'No status';
        break;
      case 'lead':
        key = it.project.lead_id ?? 'unassigned';
        label = it.project.lead_id ? it.leadName ?? 'Unknown' : 'No lead';
        break;
      default:
        key = 'all';
        label = '';
    }
    let entry = groups.get(key);
    if (!entry) {
      entry = { label, rows: [] };
      groups.set(key, entry);
    }
    entry.rows.push(it);
  }
  return Array.from(groups.entries())
    .map(([key, v]) => ({ key, label: v.label, rows: v.rows }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Gallery view for `/projects`. Renders a responsive card grid; each card
 * shows the project name plus a subset of chips chosen by the Properties
 * picker (Status / Lead / Progress / Definition of Done / Target date /
 * Description). Mirrors `DocumentsGallery` so the two surfaces share the same
 * grouped-grid shape and visual language.
 */
export function ProjectsGallery({
  rows,
  visibleChips,
  groupBy,
  onRowOpen
}: ProjectsGalleryProps) {
  const groups = useMemo(() => groupRows(rows, groupBy), [rows, groupBy]);

  const showChip = (k: GalleryChipKey) => visibleChips.includes(k);

  return (
    <div className="pn-projects-gallery" data-testid="projects-gallery">
      {groups.map((g) => (
        <section
          key={g.key}
          className="pn-projects-gallery-group"
          data-testid={`projects-gallery-group-${g.key}`}
        >
          {groupBy !== 'none' ? (
            <header
              className="pn-projects-gallery-group-head"
              data-testid={`projects-gallery-group-head-${g.key}`}
            >
              {g.label}
              <span className="pn-projects-gallery-group-count">
                {g.rows.length}
              </span>
            </header>
          ) : null}
          <div className="pn-projects-grid">
            {g.rows.map(({ project, leadName, doneCount, totalCount, dodMet, dodTotal }) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="pn-project-card"
                data-testid={`project-card-${project.id}`}
                onClick={() => onRowOpen(project.id)}
              >
                <div className="pn-project-card-top">
                  {showChip('status') ? (
                    <Pill status={statusToPill(project.status)}>
                      {project.status}
                    </Pill>
                  ) : (
                    <span />
                  )}
                  {showChip('dod') ? (
                    <MonoLabel size="xs">{dodMet}/{dodTotal} DoD</MonoLabel>
                  ) : null}
                </div>
                <div className="pn-project-card-title">{project.name}</div>
                {showChip('description') && project.description ? (
                  <p className="pn-project-card-desc">{project.description}</p>
                ) : null}
                <div className="pn-project-card-foot">
                  {showChip('lead') ? (
                    <MonoLabel size="xs" tone="muted">
                      {leadName ?? 'No lead'}
                    </MonoLabel>
                  ) : (
                    <span />
                  )}
                  {showChip('target_date') && project.target_date ? (
                    <MonoLabel size="xs" tone="muted">
                      {project.target_date}
                    </MonoLabel>
                  ) : null}
                  {showChip('progress') ? (
                    <MonoLabel size="xs">{doneCount}/{totalCount} done</MonoLabel>
                  ) : null}
                </div>
                {showChip('progress') ? (
                  <ProgressBar
                    value={doneCount}
                    total={totalCount}
                    label={`${project.name} progress`}
                    size="sm"
                  />
                ) : null}
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
